import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSelect, SelectOption } from '@percent/lemonade';
import { OrganisationSelectProps } from './OrganisationSelect.types';

export const ORGANISATION_SEARCH_MIN_LENGTH = 3;

export function OrganisationSelect({
  name,
  setOrganisationValue,
  setQueryValue,
  countryCode,
  placeholder,
  query = '',
  organisation: selectedOrganisation,
  data,
  isLoading,
  onAddNonProfit,
}: OrganisationSelectProps) {
  const { t } = useTranslation();

  const selectOptions: SelectOption[] = useMemo(
    () =>
      data?.map((org) => ({
        value: org.id,
        label: org.name,
        description: [org.address, org.postcode].filter(Boolean).join(', '),
      })) ?? [],
    [data],
  );

  const onOptionSelected = (organisation: SelectOption | null) => {
    if (organisation) {
      setOrganisationValue(data?.find((org) => org.id === organisation?.value));
    }
  };

  const hasNotSearchedYet = query.trim().length < ORGANISATION_SEARCH_MIN_LENGTH;

  return (
    <AsyncSelect
      name={name}
      key={countryCode}
      placeholder={placeholder}
      onChange={onOptionSelected}
      defaultValue={{ value: selectedOrganisation?.id ?? '', label: selectedOrganisation?.name ?? '' }}
      options={selectOptions}
      setQuery={setQueryValue}
      query={selectedOrganisation?.name ?? query}
      loading={!hasNotSearchedYet && isLoading && !selectedOrganisation}
      data-testid="organisationSearch"
      minSearchLength={ORGANISATION_SEARCH_MIN_LENGTH}
      minSearchLengthMessage={t('hostedValidation.typography.minSearchLengthMessage', {
        count: ORGANISATION_SEARCH_MIN_LENGTH,
      })}
      noResultsFoundText={hasNotSearchedYet ? t('hostedValidation.typography.startTypingHint') : undefined}
      addOption={
        hasNotSearchedYet
          ? undefined
          : {
              label: t('hostedValidation.button.cantFindNonprofit'),
              onClick: () => onAddNonProfit(),
            }
      }
    />
  );
}
